import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChatMessages } from './ChatMessages';
import QRCode from 'qrcode';
import { notify } from '../../utils/toast';
import { useTheme } from '../../hooks';
import { ChatInput } from './ChatInput';
import { ToolMode, DEFAULT_TOOL_MODES } from '../../constants/toolModes';
import { SuggestionGrid } from '../suggestions/SuggestionGrid';
import { HeaderActions } from '../header/HeaderActions';
import { Message } from '../../types/chat';
import { AnimatedAvatar } from '../agents/AnimatedAvatar';
import { useAgent } from '../../contexts/AgentContext';
import { ShareQRCodeDialog } from './ShareQRCodeDialog';
import { UnshareDialog } from './UnshareDialog';
import { Typewriter } from "react-simple-typewriter";

interface ChatContainerProps {
  messages: Message[];
  loading?: boolean;
  sending?: boolean;
  onSend: (content: string, tools?: ToolMode[]) => Promise<void>;
  onStop?: () => void;
  onRegenerate?: (index: number) => void;
  currentConversation: {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
    isPinned?: boolean;
  } | null;
  onTogglePin?: () => void;
  tools?: ToolMode[];
  onToolsChange?: (tools: ToolMode[]) => void;
  isSharedConversation?: string;
  onSyncConversations?: () => Promise<void>;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  loading,
  sending,
  onSend,
  onStop,
  onRegenerate,
  currentConversation,
  onTogglePin,
  tools: externalTools,
  onToolsChange,
  isSharedConversation,
  onSyncConversations
}) => {
  const [temporaryMessage, setTemporaryMessage] = useState<Message | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showUnShareDialog, setShowUnShareDialog] = useState(false);
  const { theme } = useTheme();
  const { mainAgent, openDialog } = useAgent();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const generateQRCode = useCallback(async (url: string) => {
    const canvas = document.createElement('canvas');
    const qrSize = 400;
    canvas.width = qrSize;
    canvas.height = qrSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Generate QR code
    const qrDataUrl = await QRCode.toDataURL(url, {
      width: qrSize,
      margin: 2,
      color: {
        dark: isDark ? '#ffffff' : '#000000',
        light: isDark ? '#1f2937' : '#ffffff'
      },
      errorCorrectionLevel: 'H'
    });

    // Draw QR code on canvas
    const qrImage = new Image();
    await new Promise((resolve, reject) => {
      qrImage.onload = resolve;
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });
    ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);

    // Draw logo
    const logoSize = 80;
    const logo = new Image();
    await new Promise((resolve, reject) => {
      logo.onload = resolve;
      logo.onerror = reject;
      logo.src = '/images/Agent.png';
    });
    const logoX = (qrSize - logoSize) / 2;
    const logoY = (qrSize - logoSize) / 2;
    
    // Draw white background for logo
    ctx.fillStyle = isDark ? '#1f2937' : '#ffffff';
    ctx.fillRect(logoX - 5, logoY - 5, logoSize + 10, logoSize + 10);
    
    // Draw logo
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

    return canvas.toDataURL();
  }, [isDark]);
  // Use external tools if provided, otherwise use default
  const [tools, setTools] = useState<ToolMode[]>(externalTools || DEFAULT_TOOL_MODES);
  
  // Update local tools state when external tools change
  useEffect(() => {
    if (externalTools) {
      setTools(externalTools);
    }
  }, [externalTools]);
  
  // Handle tool changes
  const handleToolsChange = useCallback((newTools: ToolMode[]) => {
    setTools(newTools);
    if (onToolsChange) {
      // Ensure changes are propagated to parent components
      onToolsChange(newTools);
    }
  }, [onToolsChange]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const showSuggestions = messages.length === 0 && !loading && !sending;

  const isNearBottom = () => {
    const container = document.querySelector('.scrollbar-custom');
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  };

  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      // Only scroll if user is already near bottom
      if (isNearBottom()) {
        messagesEndRef.current.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'end'
        });
      }
    }
  };

  // Scroll on initial load and new messages (always scroll on initial load)
  useEffect(() => {
    if (messages.length === 1) {
      // Force scroll on first message
      messagesEndRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'end'
      });
    } else {
      scrollToBottom(false);
    }
  }, [messages.length]);

  // Scroll when content updates during streaming
  useEffect(() => {
    if (sending && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.sender === 'assistant') {
        scrollToBottom(true);
      }
    }
  }, [sending, messages]);

  // Listen for message completion event
  useEffect(() => {
    const handleMessageComplete = () => {
      // Only scroll if user is already near bottom
      if (isNearBottom()) {
        scrollToBottom(true);
      }
    };
    window.addEventListener('message-complete', handleMessageComplete);
    return () => window.removeEventListener('message-complete', handleMessageComplete);
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between">
          <h2 className="text-md  text-gray-900 dark:text-white truncate">
            {/* 'Select one of quick dial suggestions or edit them if you like' */}
            {currentConversation?.title ||  <p className="text-gray-600 dark:text-gray-400 mb-4">
                        <Typewriter
                          words={[
                            "Use text or voice to create and send prompts to Agency",
                       
                             "Select one of quick dial suggestions or edit them if you like",
                              "Add more specialized compound (packed Agent and MCP tools) to use ecosystem toolings",
                        
                            
                          ]}
                          loop={0}
                          cursor={true}
                          cursorStyle="|"
                          typeSpeed={70}
                          deleteSpeed={10}
                          delaySpeed={1000}
                        />
                      </p>}
          </h2>
          {currentConversation?.title && currentConversation?.title !== 'New Conversation' && (<HeaderActions 
            currentConversation={currentConversation}
            messages={messages}
            isSharedConversation={isSharedConversation}
            onShare={() => {
              if (currentConversation) {
                setShowShareDialog(true);
              } else {
                notify.error('No conversation to share');
              }
            }}
            onUnShare={() => {
              if (currentConversation) {
                setShowUnShareDialog(true);
              } else {
                notify.error('No conversation to unshare');
              }
            }}
            onTogglePin={onTogglePin}
          />)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 min-h-0 scrollbar-custom">
        {showSuggestions ? (
          <div className="h-full flex flex-col items-center justify-center p-4 md:p-6">
            <div className="w-48 h-48 mb-8 flex items-center justify-center cursor-pointer" onClick={openDialog}>
              <AnimatedAvatar 
                src={mainAgent.avatar}
                alt={`${mainAgent.name} Icon`}
                size="xl"
                enabled={mainAgent.enabled}
                onClick={openDialog}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-gray-900 dark:text-white text-center">
              How can I help you with Algorand today?
            </h1>
            <div className="w-full max-w-2xl">
              <SuggestionGrid onSelect={onSend} tools={tools} />
            </div>
          </div>
        ) : (
          <div className="p-4 md:p-6 space-y-4">
            <div className="space-y-4 w-full max-w-5xl mx-auto">
      
               <ChatMessages
                messages={messages}
                loading={loading}
                sending={sending}
                onRegenerate={onRegenerate}
                isSharedConversation={isSharedConversation}
              />
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

     {isSharedConversation !=='shared' &&( <div className="flex-shrink-0 w-full border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="w-full max-w-5xl mx-auto px-4 py-4 md:px-6 md:py-6">
          <ChatInput 
            onSend={(content) => onSend(content, tools)} 
            onStop={onStop}
            disabled={loading} 
            sending={sending}
            onToolModesChange={handleToolsChange}
            initialTools={tools}
          />
        </div>
      </div>)}
      
      {showShareDialog && currentConversation && (
        <ShareQRCodeDialog 
          conversationId={currentConversation.id}
          onClose={() => setShowShareDialog(false)}
          onSuccess={async () => {
            if (onSyncConversations) {
              await onSyncConversations();
            }
          }}
        />
      )}
      {showUnShareDialog && currentConversation && (
        <UnshareDialog 
          conversationId={currentConversation.id}
          onClose={() => setShowUnShareDialog(false)}
          onSuccess={async () => {
            if (onSyncConversations) {
              await onSyncConversations();
            }
            notify.success('Conversation unshared successfully');
          }}
        />
      )}
    </div>
  );
};
